import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, map, of, switchMap, takeUntil } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { Permission } from '../../models/common/permission.model';

@Injectable({ providedIn: 'root' })
export class PermissionService implements OnDestroy {
  private permissionsSubject = new BehaviorSubject<Permission[] | null>(null);
  private destroy$ = new Subject<void>();
  private permissionsLoaded = false;

  readonly permissions$: Observable<Permission[] | null> = this.permissionsSubject.asObservable();

  private userRoleSubject = new BehaviorSubject<string | null>(null);
  readonly userRole$: Observable<string | null> = this.userRoleSubject.asObservable();

  private userEmail: string | null = null;

  constructor(private auth0: AuthService) {

    this.setupAuthListener();
    this.auth0.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.userEmail = user?.email ?? null;
    });

  }

  ensureRoleLoaded(): Observable<string | null> {
    return this.ensurePermissionsLoaded().pipe(
      map(() => this.userRoleSubject.getValue())
    );
  }

  ensurePermissionsLoaded(): Observable<Permission[] | null> {
    if (this.permissionsLoaded) {
      return of(this.permissionsSubject.getValue());
    }
    return this.auth0.isAuthenticated$.pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(null);
        }

        return this.auth0.idTokenClaims$.pipe(
          switchMap(token => {
            if (token) {
              const claims = token as any;
              const permissions = claims?.user?.manager_permissions || claims?.manager_permissions;
              const roleName = claims?.user?.manager_role_name || claims?.manager_role_name;

              this.userRoleSubject.next(roleName);

              if (permissions) {
                this.permissionsSubject.next(permissions);
                this.permissionsLoaded = true;
                return of(permissions);
              }
              
              if(this.isAdminRole() || !permissions) {
                this.permissionsLoaded = true;
                this.permissionsSubject.next([]);
                return of([]);
              }
            }

            return of(null);
          }),
          catchError(error => {
            console.error('Erro ao carregar permissões:', error);
            return of(null);
          })
        );
      })
    );
  }

  private setupAuthListener(): void {
    this.auth0.isAuthenticated$.pipe(
      takeUntil(this.destroy$),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          this.clearPermissions();
          return of(null);
        }
        return this.auth0.idTokenClaims$.pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error('Error getting permissions from Auth0:', error);
            return of(null);
          })
        );
      })
    ).subscribe(token => {
      if (token) {
        const claims = token as any;
        const permissions = claims?.user?.manager_permissions || claims?.manager_permissions;
        if (permissions) {
          this.permissionsSubject.next(permissions);
          this.permissionsLoaded = true;
        }
      }
    });
  }

  setPermissions(permissions: Permission[]): void {
    if (!permissions) {
      return;
    }

    this.permissionsSubject.next(permissions);
  }

  getPermissions(): Permission[] | null {
    return this.permissionsSubject.getValue();
  }
  
  isAdminRole(): boolean {
    const role = this.getUserRole();
    if(!role) return false;
    if(this.getPermissions()?.length === 0) return true;//se nao vier permissoes, entao libera acesso admin
    return role.toLowerCase() === 'administrador';

  }

  getUserRole(): string | null {
    return this.userRoleSubject.getValue();
  }

  getUserEmail(): string | null {
    return this.userEmail;
  }

  hasPagePermissionAsync(target: string, action: 'read' | 'write' = 'read'): Observable<boolean> {
    return this.ensureRoleLoaded().pipe(
      switchMap(_ => {
        if (this.isAdminRole()) {
          return of(true); 
        }

        return this.ensurePermissionsLoaded().pipe(
          map(permissions => {
            if (!permissions || permissions.length === 0) {
              return true;
            }
            const pagePerm = permissions.find(
              (perm: Permission) => perm.permission_level === 'PAGE' && perm.target === target
            );

            if (!pagePerm) {
              return true;
            }
            if (action === 'read') {
              return !!pagePerm.can_read;
            } else {
              return !!pagePerm.can_write;
            }
          })
        );
      })
    );
  }

  hasPagePermission(target: string, action: 'read' | 'write' = 'read'): boolean {
    if (this.isAdminRole()) {
      return true;
    }
    const permissions = this.getPermissions();
    if (!permissions || permissions.length === 0) {
      return true;
    }
    
    const pagePerm = permissions.find(
      (perm: Permission) => perm.permission_level === 'PAGE' && perm.target === target
    );

    if (!pagePerm) {
      return true;
    }

    const hasPermission = pagePerm[`can_${action}`] === true;
    return hasPermission;
  }

  getFieldPermissions(pageTarget: string): Permission[] {
    const permissions = this.getPermissions();
    if (!permissions || permissions.length === 0) return [];
    const pagePerm = permissions.find(
      (perm: Permission) => perm.permission_level === 'PAGE' && perm.target === pageTarget
    );
    return pagePerm?.fields || [];
  }

  hasFieldPermission(
    pageTarget: string,
    field: string,
    action: 'read' | 'write' = 'read'
  ): boolean {
    const fields = this.getFieldPermissions(pageTarget);

    return fields.some(
      (permission: Permission) =>
        permission.permission_level === 'FIELD' &&
        permission.target === field &&
        permission[`can_${action}`] === true
    );
  }
  
  clearPermissions(): void {
    this.permissionsSubject.next(null);
    this.userRoleSubject.next(null);
    this.permissionsLoaded = false;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
